import React, { useRef } from 'react';
import { Container, Typography, Box, Button, Card, CardContent, useTheme } from '@mui/material';
import { Favorite as FavoriteIcon } from '@mui/icons-material';
import { motion, useInView } from 'framer-motion';
import { ContentUseCase } from '../../corelogic/usecases/ContentUseCase';
import { useLanguage } from '../../shared/contexts/LanguageContext';
import { SEO } from './SEO';
import { trackDonation, trackButtonClick } from '../../configuration/analytics';
import { getIOSButtonPrimaryStyles, getGlassCardStyles } from '../../shared/styles';

const contentUseCase = new ContentUseCase();

export const DonationPage: React.FC = () => {
  const theme = useTheme();
  const { language } = useLanguage();
  const donationContent = contentUseCase.getDonationContent(language);
  
  const headerRef = useRef(null);
  const buttonRef = useRef(null);
  const impactRef = useRef(null);
  
  const headerInView = useInView(headerRef, { once: true });
  const buttonInView = useInView(buttonRef, { once: true });
  const impactInView = useInView(impactRef, { once: true, margin: "-100px" });
  
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const }
    }
  };
  
  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.1
      }
    }
  };

  return (
    <Box>
      <SEO page="donate" />
      <Container maxWidth="xl" sx={{ py: theme.spacing(8) }}>
        <motion.div
          ref={headerRef}
          initial={{ opacity: 0, y: 20 }}
          animate={headerInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2, mb: theme.spacing(2) }}>
            <Box
              component="img"
              src="/icon.png"
              alt=""
              aria-hidden="true"
              sx={{ height: { xs: 44, md: 56 }, width: 'auto' }}
            />
            <Typography variant="h2" component="h1" color="primary" sx={{ fontWeight: 800 }}>
              {language === 'de' ? 'Spenden' : 'Donate'}
            </Typography>
          </Box>
          <Typography variant="h6" align="center" color="text.secondary" sx={{ maxWidth: 900, mx: 'auto', mb: theme.spacing(6) }}>
            {donationContent.subtitle}
          </Typography>
          {/* Description */}
          <Typography 
            variant="body1" 
            paragraph 
            align="center" 
            sx={{ mb: theme.spacing(6), fontSize: '1.125rem', color: 'text.secondary', maxWidth: 900, mx: 'auto' }}
          >
            {donationContent.description}
          </Typography>
        </motion.div>

        {/* Main Donation Button */}
        <motion.div
          ref={buttonRef}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={buttonInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.6, delay: 0.3, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <Box sx={{ textAlign: 'center', mb: theme.spacing(8) }}>
            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ duration: 0.2 }}
            >
              <Button
                size="large"
                href={donationContent.mainDonationButton.url}
                target="_blank"
                rel="noopener noreferrer"
                startIcon={<FavoriteIcon />}
                onClick={() => {
                  trackDonation(undefined, 'main_donation_button');
                  trackButtonClick('main_donation_button', '/donate');
                }}
                sx={{ 
                  px: theme.spacing(6), 
                  py: theme.spacing(2.5), 
                  fontSize: '1.25rem',
                  ...getIOSButtonPrimaryStyles(theme),
                }}
              >
                {donationContent.mainDonationButton.title}
              </Button>
            </motion.div>
          </Box>
        </motion.div>

        {/* Impact Section */}
        <Box
          sx={{
            width: '100px',
            height: '2px',
            backgroundColor: 'secondary.main',
            mx: 'auto',
            mb: theme.spacing(6),
          }}
        />
        <motion.div
          ref={impactRef}
          initial={{ opacity: 0, y: 30 }}
          animate={impactInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <Typography variant="h2" component="h2" gutterBottom align="center" sx={{ mb: theme.spacing(6) }}>
            {donationContent.impactSection.title}
          </Typography>
        </motion.div>

        <motion.div
          initial="hidden"
          animate={impactInView ? "visible" : "hidden"}
          variants={staggerContainer}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: theme.spacing(4),
            }}
          >
            {donationContent.impactSection.cards.map((card, idx) => (
              <motion.div
                key={idx}
                variants={fadeInUp}
                style={{ height: '100%' }}
              >
                <Card sx={{ height: '100%', ...getGlassCardStyles(theme, 'default', true) }}>
                  <CardContent sx={{ p: theme.spacing(4) }}>
                    <Typography variant="h4" component="h3" gutterBottom color="secondary">
                      {card.title}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {card.description}
                    </Typography>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Container>
    </Box>
  );
};

